import { useFormatDate } from '@/composables/useFormatDate'
import { useTransactionStore } from '@/stores/transaction'
import { useDetailStore } from '@/stores/useDetailStore'
import { useEvaluationStore } from '@/stores/useEvaluationStore'
import { useInfraStore } from '@/stores/useInfraStore'
import { useHelpers } from './useHelpers'

// RED-279: pick the first source that actually holds a value. The LandBoard
// payload pre-fills rating keys with '' (LandBoardService), so a plain `??`
// chain never reached the QV-persisted value in `technical`.
const firstFilled = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined || value === '') continue
    if (Array.isArray(value) && value.length === 0) continue
    return value
  }
  return null
}

export function useTransactionComparisonMasterDetails() {
  const transactionStore = useTransactionStore()
  const evaluationStore = useEvaluationStore()
  const detailStore = useDetailStore()
  const infraStore = useInfraStore()
  const {
    composeData,
    formatText,
    formatFloorFromFloors,
    formatTechnical,
    formatLandTechnical,
  } = useHelpers()
  const { formatDate } = useFormatDate()

  const storeMasterData = () => {
    const masterData = transactionStore.masterData || composeData(null, null)
    const detailData = collectMasterDetails()
    transactionStore.masterData = {
      ...masterData,
      ...detailData,
      // RED-313: session edits of subject cells win over the recomposed
      // detail values (otherwise every remount wipes them).
      ...transactionStore.masterOverrides,
    }
  }

  const collectMasterDetails = () => {
    const main = detailStore.mainForm
    const hooneosa = evaluationStore.selectedBuildingPart
    const technical = detailStore.ehrTechnical
    const communal = detailStore.ehrCommunal
    const interior = detailStore.interior
    const construction = detailStore.ehrConstruction
    const landBoard = detailStore.landBoard
    return {
      aol_kpv: main?.vaartuse_kuupaev
        ? formatDate(main?.vaartuse_kuupaev)
        : null,
      aadress: main?.aadress + (hooneosa?.['ehitise-osa-number'] ? '-' + hooneosa?.['ehitise-osa-number'] : '') ?? null,
      kandekonstruktsioon:
        formatText(construction?.konstruktsioonide_materjal ?? '') ?? null,
      ehitise_esmane_kasutus:
        detailStore.ehrGeneral?.['kasutuselevotu-aasta'] ?? null,
      ehitise_seisukord: construction?.ehitise_seisukord ?? null,
      korteri_pind:
        main?.eriomandi_pind || hooneosa?.['ehitise-osa-pind'] || null,
      hooneosa_pind: hooneosa?.['ehitise-osa-pind'] ?? null,
      // RED-235: subject building SNP for the dual "SNP / dwelling area" row
      // (consumed by useTransactionStore comparisonTableRows when isSubjectHouse).
      ehitise_suletud_netopind: technical?.snp ?? null,
      hooneosa_tubade_arv: hooneosa?.['ehitise-osa-tubade-arv'] ?? null,
      hooneosa_seisukord: interior?.hooneosa_seisukord ?? null,
      korruste_arv:
        hooneosa && technical
          ? formatFloorFromFloors(
              hooneosa['ehitise-osa-korrus'],
              technical['maapealseid-korruseid'],
            )
          : null,
      // RED-267: land mode composes Tehnovõrgud from the per-network communal
      // fields (same formatter as a comparable's maatukk keys — the composite
      // strings stay comparable); built mode keeps the classic 3-field format.
      tehnovorgud: evaluationStore.isLandParcel
        ? formatLandTechnical(communal)
        : communal
          ? formatTechnical(
              communal.veevarustuse_liik,
              communal.kanalisatsiooni_liik,
              communal.ventilatsiooni_liik,
            )
          : null,
      hooneosa_soojavarustuse_liik:
        hooneosa?.['ehitise-osa-soojavarustuse-liik'] ?? null,
      koogimoobel: interior?.kohtkindel_sisseseade ?? null,
      // QV wizard persists parkimine/panipaik on `evaluation.technical`, while
      // Detail flow stores parkimine on landBoard and panipaik on construction.
      // Fall back to `technical` so the comparison-table master cell populates
      // for QV runs — otherwise DocxService strips the 3-row groups from export.
      kinnistu_parkimine: firstFilled(landBoard?.kinnistu_parkimine, technical?.kinnistu_parkimine),
      rodu: construction?.rodu ?? null,
      panipaik: construction?.panipaik ?? technical?.panipaik ?? null,
      maatuki_pindala: landBoard?.katastriyksuse_pindala_raw ?? null,
      // RED-267 land elements — subject values from the Detail-view cards that
      // already own their editing + persistence (infra, plannings, Kiirpäring
      // maakatastrist). Master-cell edits route back to the same homes via
      // MASTER_LAND_FIELDS.
      juurdepaas: infraStore.infra?.juurdepaas ?? null,
      ehitusoigus: landBoard?.detailplaneering ?? null,
      haljastus: landBoard?.kinnistu_haljastus ?? null,
    }
  }

  return {
    storeMasterData,
  }
}
